import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import type { PartCatalogSummaryDto, PartLotSummaryDto, RobotPartDto } from '@bhops/shared';
import { partsApi } from '@/api/parts';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, FormError, Input, Select } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { useToast } from '@/components/Toast';
import { CATEGORY_LABEL, SLOT_SUGGESTIONS, defaultSlots } from './part-slots';

// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.5 — 부품 장착 확인 창.
//
// 2026-10-28 오너 — 부품은 왼쪽 목록에서 로봇 그림으로 끌어다 놓아 장착한다(여러 개 한꺼번에).
// 놓은 부품마다 슬롯(분류별 기본값)과 LOT(선택)만 확인하고 [장착]을 누른다. 놓자마자 바로 쓰지
// 않는 이유: 같은 슬롯의 기존 부품은 서버가 UPGRADE 사유로 자동 제거하고, LOT 을 고르면 재고가
// 빠진다 — 둘 다 되돌리기 번거로운 기록이라 한 번 보여 주고 쓴다.

interface Row {
  catalog: PartCatalogSummaryDto;
  slot: string;
  partLotId: string;
}

export interface PartInstallModalProps {
  /** 장착할 카탈로그 부품. 비어 있으면 창이 닫혀 있다. */
  catalogs: PartCatalogSummaryDto[];
  onClose: () => void;
  robotId: string;
  /** 지금 끼워진 부품 — 슬롯이 겹치면 무엇이 교체되는지 알려 준다. */
  activeParts: RobotPartDto[];
  /** 장착된 부품(서버가 돌려준 RobotPart id). 중간에 멈췄어도 장착된 만큼 알린다. */
  onInstalled?: (robotPartIds: string[]) => void;
}

export function PartInstallModal({
  catalogs,
  onClose,
  robotId,
  activeParts,
  onInstalled,
}: PartInstallModalProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const open = catalogs.length > 0;
  const [rows, setRows] = useState<Row[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const slots = defaultSlots(catalogs);
    setRows(catalogs.map((catalog, i) => ({ catalog, slot: slots[i], partLotId: '' })));
    setError(null);
  }, [open, catalogs]);

  // LOT — 부품마다 재고가 남은 것만.
  const lotQs = useQueries({
    queries: rows.map((r) => ({
      queryKey: ['parts', 'lots', { partCatalogId: r.catalog.id }],
      queryFn: () => partsApi.listLots({ partCatalogId: r.catalog.id }),
      staleTime: 30_000,
    })),
  });

  const activeBySlot = useMemo(() => new Map(activeParts.map((p) => [p.slot, p])), [activeParts]);

  const slotProblem = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows) {
      const s = r.slot.trim();
      if (s.length < 2) return '슬롯을 두 글자 이상 입력해 주세요.';
      if (seen.has(s))
        return `슬롯 ${s} 가 두 번 들어 있습니다. 한 슬롯에는 부품 하나만 장착됩니다.`;
      seen.add(s);
    }
    return null;
  }, [rows]);

  function update(i: number, patch: Partial<Row>): void {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  function handleClose(): void {
    if (pending) return;
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (slotProblem || pending) return;
    setError(null);
    setPending(true);
    // 하나씩 차례로 — 서버의 슬롯 자동 교체가 순서에 기대므로 동시에 보내지 않는다.
    let done = 0;
    const created: string[] = [];
    try {
      for (const r of rows) {
        const part = await partsApi.installPart(robotId, {
          partCatalogId: r.catalog.id,
          partLotId: r.partLotId || undefined,
          slot: r.slot.trim(),
        });
        if (part?.id) created.push(part.id);
        done += 1;
      }
    } catch (err) {
      // 이미 끼운 줄은 빼서, 다시 누르면 남은 것만 장착되게 한다.
      setRows((rs) => rs.slice(done));
      const failed = rows[done];
      let reason = extractApiError(err);
      // 화면에 든 목록이 오래된 사이 부품이나 LOT 이 지워지거나 꺼진 경우 — 서버 글(영문 id)
      // 대신 무슨 일인지 말하고 목록을 새로 받는다(2026-10-28 오너가 "PartCatalog … not found" 를 봄).
      if (/^Part(Catalog|Lot) \S+ not found$/.test(reason)) {
        const what = reason.startsWith('PartLot')
          ? '목록에서 빠진 LOT입니다.'
          : `목록에서 빠진 부품입니다: ${failed?.catalog.displayName ?? '알 수 없음'}.`;
        reason = `${what} 지워졌거나 사용 중지된 것 같아 목록을 새로 불러왔습니다. 창을 닫고 다시 골라 주세요.`;
        qc.invalidateQueries({ queryKey: ['parts', 'catalog'] });
        qc.invalidateQueries({ queryKey: ['parts', 'lots'] });
      }
      setError(done > 0 ? `${done}개를 장착한 뒤 멈췄습니다. ${reason}` : reason);
    } finally {
      setPending(false);
      if (done > 0) {
        qc.invalidateQueries({ queryKey: ['robot-parts', robotId] });
        qc.invalidateQueries({ queryKey: ['parts', 'lots'] });
        qc.invalidateQueries({ queryKey: ['parts', 'catalog'] });
        qc.invalidateQueries({ queryKey: ['part-lots'] });
        onInstalled?.(created);
      }
    }
    if (done === rows.length) {
      toast.success(done > 1 ? `부품 ${done}개를 장착했습니다.` : '부품을 장착했습니다.');
      onClose();
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={rows.length > 1 ? `부품 ${rows.length}개 장착` : '부품 장착'}
      width={640}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={pending}>
            취소
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={pending || slotProblem !== null || rows.length === 0}
            data-testid="part-install-submit"
          >
            {pending ? '장착 중…' : '장착'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {error && <FormError>{error}</FormError>}

        {rows.map((r, i) => {
          const lots = (lotQs[i]?.data ?? []).filter((l) => l.remainingQty > 0);
          const replaced = activeBySlot.get(r.slot.trim());
          return (
            <div
              key={r.catalog.id}
              style={rowStyle}
              data-testid={`part-install-row-${r.catalog.id}`}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{r.catalog.displayName}</span>
                <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {CATEGORY_LABEL[r.catalog.category]} · SKU {r.catalog.sku}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8 }}>
                <label style={fieldLabel}>
                  슬롯
                  <Input
                    list="part-slot-suggestions"
                    value={r.slot}
                    onChange={(e) => update(i, { slot: e.target.value.toUpperCase() })}
                    aria-label={`${r.catalog.displayName} 슬롯`}
                    data-testid={`part-slot-input-${r.catalog.id}`}
                  />
                </label>
                <label style={fieldLabel}>
                  LOT (선택)
                  <Select
                    value={r.partLotId}
                    onChange={(e) => update(i, { partLotId: e.target.value })}
                    aria-label={`${r.catalog.displayName} LOT`}
                    data-testid={`part-lot-select-${r.catalog.id}`}
                  >
                    <option value="">LOT 없이 장착, 재고 추적 안 함</option>
                    {lots.map((l) => (
                      <LotOption key={l.id} lot={l} />
                    ))}
                  </Select>
                </label>
              </div>
              {replaced && (
                <div style={replaceNote}>
                  <Icon name="swap" size={13} aria-hidden />
                  <span>
                    이 슬롯에 끼워진 부품을 업그레이드 사유로 빼고 바꿉니다:{' '}
                    <strong>{replaced.partCatalog.displayName}</strong>
                  </span>
                </div>
              )}
            </div>
          );
        })}
        <datalist id="part-slot-suggestions">
          {SLOT_SUGGESTIONS.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>

        {slotProblem && rows.length > 0 && <FormError>{slotProblem}</FormError>}
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
          결함이나 마모 같은 다른 사유로 빼려면 장착하기 전에 오른쪽 장착 목록에서 [제거]를 먼저
          눌러 주세요.
        </div>
      </form>
    </Modal>
  );
}

function LotOption({ lot }: { lot: PartLotSummaryDto }) {
  const isConfirmed = lot.defectStatus === 'CONFIRMED';
  const warn = lot.defectStatus === 'SUSPECTED' ? ', 결함 의심' : isConfirmed ? ', 결함 확정' : '';
  const title = isConfirmed
    ? '결함이 확정된 LOT이라 설치할 수 없습니다. 이 LOT의 결함 상태를 해제한 뒤에 사용해 주세요.'
    : undefined;
  return (
    <option value={lot.id} disabled={isConfirmed} title={title}>
      {lot.lotCode} · 잔여 {lot.remainingQty}/{lot.receivedQty}
      {warn}
    </option>
  );
}

const rowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};

const fieldLabel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: 'var(--fg-muted)',
};

const replaceNote: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  padding: '6px 8px',
  background: 'var(--bg-warning-subtle)',
  border: '1px solid var(--severity-warning)',
  borderRadius: 'var(--r-card)',
};
